export const OFFLINE_REQUIRED = 'OFFLINE_REQUIRED';
export const MUTATION_CONFIRMATION_UNKNOWN = 'MUTATION_CONFIRMATION_UNKNOWN';
export const REMOTE_SERVICE_UNAVAILABLE = 'REMOTE_SERVICE_UNAVAILABLE';
export const REMOTE_CONFIRMATION_TIMEOUT_MS = 15000;

let deviceOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
let serviceAvailable = null;
let accountDeletionMutationBlock = false;
let snapshot = { deviceOnline, serviceAvailable };
const listeners = new Set();

const publish = () => { snapshot = { deviceOnline, serviceAvailable }; listeners.forEach((listener) => listener()); };
export const setDeviceOnline = (value) => { deviceOnline = Boolean(value); if (!deviceOnline) serviceAvailable = null; publish(); };
export const markServiceAvailable = () => { serviceAvailable = true; publish(); };
export const markServiceUnavailable = () => { serviceAvailable = false; publish(); };
export const getConnectivityState = () => snapshot;
export const subscribeConnectivity = (listener) => { listeners.add(listener); return () => listeners.delete(listener); };

export class RemoteOperationError extends Error {
  constructor(code, settlement) { super(code); this.code = code; this.settlement = settlement; this.name = 'RemoteOperationError'; }
}

export const requireConnectionForRemoteAccess = () => {
  if (!deviceOnline || (typeof navigator !== 'undefined' && navigator.onLine === false)) throw new RemoteOperationError(OFFLINE_REQUIRED);
};

export const requireConnectionForMutation = () => {
  if (accountDeletionMutationBlock) throw new RemoteOperationError('ACCOUNT_DELETION_IN_PROGRESS');
  requireConnectionForRemoteAccess();
};

export const setAccountDeletionMutationBlock = (blocked) => { accountDeletionMutationBlock = Boolean(blocked); };

export const awaitRemoteConfirmation = async (operation, timeoutMs = REMOTE_CONFIRMATION_TIMEOUT_MS) => {
  let timer;
  const settlement = operation.then((value) => { markServiceAvailable(); return value; }, (error) => {
    const code = String(error?.code || '');
    if (/unavailable|network|deadline-exceeded/.test(code)) markServiceUnavailable();
    throw error;
  });
  try {
    const value = await Promise.race([
      settlement,
      new Promise((_, reject) => { timer = setTimeout(() => reject(new RemoteOperationError(MUTATION_CONFIRMATION_UNKNOWN, settlement)), timeoutMs); }),
    ]);
    return value;
  } catch (error) {
    if (error instanceof RemoteOperationError) throw error;
    throw error;
  } finally { clearTimeout(timer); }
};

const remoteOperations = new Map();
export const runRemoteOperation = (key, start, timeoutMs = REMOTE_CONFIRMATION_TIMEOUT_MS) => {
  let record = remoteOperations.get(key);
  if (!record) {
    record = { settlement: Promise.resolve().then(start) };
    remoteOperations.set(key, record);
    void record.settlement.catch(() => { if (remoteOperations.get(key) === record) remoteOperations.delete(key); });
  }
  return awaitRemoteConfirmation(record.settlement, timeoutMs);
};
export const getRemoteSettlement = (error) => error instanceof RemoteOperationError
  && error.code === MUTATION_CONFIRMATION_UNKNOWN && error.settlement ? error.settlement : null;

export const remoteOperationMessage = (error, fallback) => {
  const code = error?.code || error?.message;
  if (code === OFFLINE_REQUIRED) return 'Esta operação exige conexão. Os dados informados foram mantidos.';
  if (code === MUTATION_CONFIRMATION_UNKNOWN) return 'A confirmação não chegou. A operação pode ter sido recebida; aguarde a reconexão antes de tentar novamente.';
  if (code === REMOTE_SERVICE_UNAVAILABLE) return 'O serviço está indisponível agora. Tente novamente mais tarde.';
  return fallback;
};
